import { useState, useEffect, useRef, type CSSProperties } from "react";
import type { EntityTypeDefinition } from "../../../lib/types";
import { useDeleteEntityType } from "../../../lib/hooks/use-kuvio";
import { mcpClient } from "../../../lib/mcp-client";
import { showToast, CopyableCode } from "../primitives";
import { typeModuleOwner, moduleLabel } from "../../lib/module-ownership";

export interface BackRefType {
  name: string;
  slug: string;
  fields: string[];
}

interface ConfirmDeleteTableModalProps {
  open: boolean;
  onClose: () => void;
  entityType: EntityTypeDefinition;
  /** Active record count — what gets permanently deleted. */
  recordCount: number;
  /** Other entity types whose reference fields point at this one. */
  backRefTypes: BackRefType[];
  /** Called after a successful delete (navigate away — the table is gone). */
  onDeleted: () => void;
}

/**
 * Sprint 14a — type-the-name confirmation for an irreversible table delete
 * (GitHub / Stripe / Linear convention). Surfaces the row count and which other
 * tables reference this one. On confirm it first clears those references via
 * `unlink_entity_type_references` (otherwise `delete_entity_type` refuses), then
 * deletes the type.
 */
export function ConfirmDeleteTableModal({
  open,
  onClose,
  entityType,
  recordCount,
  backRefTypes,
  onDeleted,
}: ConfirmDeleteTableModalProps) {
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const deleteType = useDeleteEntityType();

  useEffect(() => {
    if (open) {
      setTyped("");
      setError(null);
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  // №11.3.2 — a module-owned type can't be deleted from the workspace (owner
  // included). Show the lock up front rather than let the user type-to-confirm
  // into a server refusal.
  const ownerModule = typeModuleOwner(entityType.fields);

  const confirmed = typed.trim() === entityType.name;

  async function handleDelete() {
    if (!confirmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (backRefTypes.length > 0) {
        const res = await mcpClient.toolsCall("unlink_entity_type_references", {
          target_type_slug: entityType.slug,
          confirm: true,
        });
        if (res.isError) throw new Error("Couldn't clear references from other tables.");
      }
      await deleteType.mutateAsync(entityType.slug);
      showToast(`Table "${entityType.name}" deleted`);
      onDeleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
      setBusy(false);
    }
  }

  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    fontFamily: "var(--font-sans)",
  };
  const modal: CSSProperties = {
    background: "var(--color-background-primary)",
    border: "0.5px solid var(--color-border-secondary)",
    borderRadius: "var(--border-radius-lg)",
    padding: 24,
    width: 440,
    maxWidth: "90vw",
  };
  const inputStyle: CSSProperties = {
    width: "100%",
    fontSize: 13,
    padding: "8px 10px",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-sm)",
    background: "var(--color-background-primary)",
    color: "var(--color-text-primary)",
    fontFamily: "var(--font-sans)",
    boxSizing: "border-box",
  };
  const btnBase: CSSProperties = {
    fontSize: 13,
    padding: "8px 16px",
    borderRadius: "var(--border-radius-md)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
    fontWeight: 500,
    border: "none",
  };

  if (ownerModule) {
    return (
      <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
        <div role="dialog" aria-modal="true" aria-label="Table is module-owned" style={modal}>
          <h2 style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 18, fontWeight: 500, margin: "0 0 8px 0", color: "var(--color-text-primary)" }}>
            <i className="ti ti-lock" aria-hidden="true" /> "{entityType.name}" is part of {moduleLabel(ownerModule)}
          </h2>
          <div style={{ fontSize: 14, color: "var(--color-text-secondary)", lineHeight: 1.55, marginBottom: 20 }}>
            This table is owned by the <strong style={{ color: "var(--color-text-primary)" }}>{moduleLabel(ownerModule)}</strong> module
            and can't be deleted from the workspace. To remove it, uninstall the module (a guarded action that warns about
            dependent modules and data) or ask the module's publisher. You can still add your own fields — they stay yours
            and are fully editable.
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button onClick={onClose} style={{ ...btnBase, background: "var(--color-accent, #2563eb)", color: "#fff" }}>
              Got it
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label="Delete table" style={modal}>
        <h2 style={{ fontSize: 18, fontWeight: 500, margin: "0 0 8px 0", color: "var(--color-text-primary)" }}>
          Delete table "{entityType.name}"?
        </h2>
        <div style={{ fontSize: 14, color: "var(--color-text-secondary)", lineHeight: 1.5, marginBottom: 16 }}>
          This permanently deletes the table and all{" "}
          <strong style={{ color: "var(--color-text-primary)" }}>
            {recordCount} {recordCount === 1 ? "row" : "rows"}
          </strong>{" "}
          in it. This cannot be undone.
          {backRefTypes.length > 0 && (
            <div style={{ marginTop: 12 }}>
              Reference fields pointing at this table will be cleared from{" "}
              {backRefTypes.map((t, i) => (
                <span key={t.slug}>
                  {i > 0 ? ", " : ""}
                  <strong style={{ color: "var(--color-text-primary)" }}>{t.name}</strong>
                  {t.fields.length > 0 ? ` (${t.fields.join(", ")})` : ""}
                </span>
              ))}
              .
            </div>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4, fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 6 }}>
          Type <CopyableCode text={entityType.name} label="Copy name" /> to confirm
        </div>
        <input
          ref={inputRef}
          type="text"
          style={inputStyle}
          value={typed}
          onChange={(e) => {
            setTyped(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && confirmed) handleDelete();
            if (e.key === "Escape" && !busy) onClose();
          }}
          placeholder={entityType.name}
          aria-label="Table name confirmation"
        />

        {error && <div style={{ fontSize: 13, color: "var(--c-red-600)", marginTop: 12 }}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          <button
            onClick={onClose}
            disabled={busy}
            style={{ ...btnBase, background: "transparent", color: "var(--color-text-secondary)" }}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={!confirmed || busy}
            style={{
              ...btnBase,
              background: "var(--c-red-600)",
              color: "#fff",
              opacity: !confirmed || busy ? 0.5 : 1,
              cursor: !confirmed || busy ? "not-allowed" : "pointer",
            }}
          >
            {busy ? "Deleting..." : "Delete table"}
          </button>
        </div>
      </div>
    </div>
  );
}
